// HubPartners — displayed inside the Ravencross Commons under the online
// panel. Reads `/api/gm/partners`, renders a horizontal scroll of cards
// with the admin's uploaded artwork, work name, title, blurb, and an
// optional external link. Silently hides itself when nothing is active.
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { ExternalLink } from "lucide-react";

export default function HubPartners() {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.hubPartners()
      .then(r => { if (alive) { setRows(r.partners || []); setLoaded(true); } })
      .catch(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, []);

  if (!loaded || rows.length === 0) return null;

  return (
    <div className="panel p-3 space-y-2" data-testid="hub-partners">
      <div className="flex items-center gap-2 border-b border-[#262626] pb-1.5">
        <div className="w-2 h-2 rounded-full bg-[#E55A00]" />
        <div className="font-heading text-xs tracking-[0.3em] uppercase text-[#E5C34A]">{t("hub.partners")}</div>
        <div className="font-mono text-[9px] uppercase tracking-widest text-[#8c8c8c] ml-auto">
          {rows.length} {rows.length === 1 ? "opera" : "opere"}
        </div>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1">
        {rows.map(p => (
          <a key={p.id}
            data-testid={`hub-partner-${p.id}`}
            href={p.link || "#"}
            target={p.link ? "_blank" : undefined}
            rel={p.link ? "noreferrer noopener" : undefined}
            onClick={e => { if (!p.link) e.preventDefault(); }}
            className={`shrink-0 w-56 panel-inner border border-[#262626] hover:border-[#E5C34A] transition-colors overflow-hidden ${p.link ? "cursor-pointer" : "cursor-default"}`}>
            {p.image ? (
              <div className="w-full h-28 bg-[#0a0a0a] overflow-hidden">
                <img src={p.image} alt={p.name}
                  className="w-full h-full object-cover"
                  onError={e => { e.currentTarget.style.opacity = 0.2; }} />
              </div>
            ) : (
              <div className="w-full h-28 bg-[#0a0a0a] flex items-center justify-center">
                <span className="font-mono text-[9px] tracking-widest uppercase text-[#333]">senza immagine</span>
              </div>
            )}
            <div className="p-2 space-y-0.5">
              <div className="font-heading text-xs tracking-widest uppercase text-[#E5C34A] truncate">{p.name}</div>
              {p.title && <div className="font-mono text-[9px] uppercase tracking-widest text-[#B89C3A] truncate">{p.title}</div>}
              {p.work_name && <div className="font-mono text-[9px] text-[#8FD1E5] truncate">{p.work_name}</div>}
              {p.description && (
                <div className="text-[10px] text-[#B0ADA5] line-clamp-2 leading-tight">{p.description}</div>
              )}
              {p.link && (
                <div className="pt-1 font-mono text-[9px] tracking-widest uppercase text-[#E55A00] flex items-center gap-1">
                  <ExternalLink className="w-2.5 h-2.5" /> {t("hub.seeWork")}
                </div>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}
